﻿import {
    IressCol,
    IressForm,
    IressFormField,
    IressFormFieldset,
    IressInput,
    IressInputCurrency,
    IressRadio,
    IressRadioGroup,
    IressRow,
} from '@iress-oss/ids-components';
import {
    CURRENCY_CODE,
    ErrorMessage,
    LOCALE,
    ProjectedMaturityBasisType,
    ProjectedValuesValues,
} from '@xplan-pension-switching/common';
import { formatTwoDecimalPlaces, twoDecimalPlaceRegex } from '../../../helpers/Numbers/TwoDecimalPlaces';
import { FC } from 'react';

export const AssumedChargesAndGrowthRatesFields: FC<{ projectedValues: ProjectedValuesValues }> = ({
    projectedValues,
}) => {
    const allChargesZeroErrorMessage = 'At least one charge must be greater than 0';

    const { clearErrors, setError, getFieldState, watch } = IressForm.useFormContext();
    const calculationMethod = watch('projectedValues.calculationMethod', projectedValues?.calculationMethod);
    const fundChargeValue = watch('projectedValues.fundCharge', projectedValues?.fundCharge);
    const planChargeValue = watch('projectedValues.planCharge', projectedValues?.planCharge);
    const policyFeeValue = watch('projectedValues.policyFee', projectedValues?.policyFee);
    const advisorChargeValue = watch('projectedValues.advisorCharge', projectedValues?.advisorCharge);

    const fields = [
        'projectedValues.fundCharge',
        'projectedValues.planCharge',
        'projectedValues.policyFee',
        'projectedValues.advisorCharge',
    ];

    const atLeastOneProductChargeGreaterThanZero = (currentValidation: string) => {
        const isValid =
            (fundChargeValue ?? 0) > 0 ||
            (planChargeValue ?? 0) > 0 ||
            (policyFeeValue ?? 0) > 0 ||
            (advisorChargeValue ?? 0) > 0;

        if (isValid) {
            // want to clear the *other* fields, leave the current validator to clear the current field
            const toClear = fields.filter(field => field !== currentValidation);
            toClear.forEach(field => {
                if (field && getFieldState(field)?.error?.message === allChargesZeroErrorMessage) {
                    clearErrors(field);
                }
            });
        } else {
            fields.forEach(field => setError(field, { message: allChargesZeroErrorMessage }));
        }

        return isValid;
    };

    return (
        <>
            <IressFormFieldset
                label="Calculation method"
                name="projectedValues.calculationMethod"
                rules={{ required: ErrorMessage.SelectAnOption }}
                defaultValue={projectedValues?.calculationMethod}
                render={controlledProps => (
                    <IressRadioGroup layout="inline" {...controlledProps}>
                        <IressRadio data-testid="rdProdCharges" value={ProjectedMaturityBasisType.UsingProductCharges}>
                            Using product charges
                        </IressRadio>
                        <IressRadio data-testid="rdAMC" value={ProjectedMaturityBasisType.CalculatedUsingAMC}>
                            Calculated using A.M.C
                        </IressRadio>
                    </IressRadioGroup>
                )}
            />
            <IressRow gutter="lg">
                {calculationMethod === ProjectedMaturityBasisType.UsingProductCharges && (
                    <>
                        <IressCol span="3">
                            <IressFormField
                                label="Fund charge"
                                name="projectedValues.fundCharge"
                                defaultValue={projectedValues?.fundCharge ?? 0}
                                rules={{
                                    pattern: twoDecimalPlaceRegex,
                                    required: ErrorMessage.EnterAValue,
                                    min: { value: 0, message: ErrorMessage.MinZero },
                                    validate: () =>
                                        atLeastOneProductChargeGreaterThanZero('projectedValues.fundCharge') ||
                                        allChargesZeroErrorMessage,
                                }}
                                render={controlledProps => (
                                    <IressInput
                                        {...controlledProps}
                                        type="number"
                                        data-testid="txtFundCharge"
                                        append="%"
                                        placeholder="0.00"
                                        formatter={value => formatTwoDecimalPlaces(value)}
                                    />
                                )}
                            />
                        </IressCol>
                        <IressCol span="3">
                            <IressFormField
                                label="Plan charge"
                                name="projectedValues.planCharge"
                                defaultValue={projectedValues?.planCharge ?? 0}
                                rules={{
                                    pattern: twoDecimalPlaceRegex,
                                    required: ErrorMessage.EnterAValue,
                                    min: { value: 0, message: ErrorMessage.MinZero },
                                    validate: () =>
                                        atLeastOneProductChargeGreaterThanZero('projectedValues.planCharge') ||
                                        allChargesZeroErrorMessage,
                                }}
                                render={controlledProps => (
                                    <IressInput
                                        {...controlledProps}
                                        type="number"
                                        data-testid="txtPlanCharge"
                                        append="%"
                                        placeholder="0.00"
                                        formatter={value => formatTwoDecimalPlaces(value)}
                                    />
                                )}
                            />
                        </IressCol>
                        <IressCol span="3">
                            <IressFormField
                                label="Policy fee"
                                name="projectedValues.policyFee"
                                defaultValue={projectedValues?.policyFee ?? 0}
                                rules={{
                                    pattern: twoDecimalPlaceRegex,
                                    required: ErrorMessage.EnterAValue,
                                    min: { value: 0, message: ErrorMessage.MinZero },
                                    validate: () =>
                                        atLeastOneProductChargeGreaterThanZero('projectedValues.policyFee') ||
                                        allChargesZeroErrorMessage,
                                }}
                                render={controlledProps => (
                                    <IressInputCurrency
                                        {...controlledProps}
                                        currencyCode={CURRENCY_CODE}
                                        locale={LOCALE}
                                        placeholder="0.00"
                                        data-testid="txtPolicyFee"
                                    />
                                )}
                            />
                        </IressCol>
                        <IressCol span="3">
                            <IressFormField
                                label="Advisor charge"
                                name="projectedValues.advisorCharge"
                                defaultValue={projectedValues?.advisorCharge ?? 0}
                                rules={{
                                    pattern: twoDecimalPlaceRegex,
                                    min: { value: 0, message: ErrorMessage.MinZero },
                                    required: ErrorMessage.EnterAValue,
                                    validate: () =>
                                        atLeastOneProductChargeGreaterThanZero('projectedValues.advisorCharge') ||
                                        allChargesZeroErrorMessage,
                                }}
                                render={controlledProps => (
                                    <IressInput
                                        {...controlledProps}
                                        type="number"
                                        data-testid="txtAdvisorCharge"
                                        append="%"
                                        placeholder="0.00"
                                        formatter={value => formatTwoDecimalPlaces(value)}
                                    />
                                )}
                            />
                        </IressCol>
                    </>
                )}
                {calculationMethod === ProjectedMaturityBasisType.CalculatedUsingAMC && (
                    <IressCol span="3">
                        <IressFormField
                            label="A.M.C"
                            name="projectedValues.amc"
                            defaultValue={projectedValues?.amc}
                            rules={{
                                required: ErrorMessage.EnterAValue,
                                pattern: twoDecimalPlaceRegex,
                                min: { value: 0, message: ErrorMessage.MinZero },
                            }}
                            render={controlledProps => (
                                <IressInput
                                    {...controlledProps}
                                    type="number"
                                    data-testid="txtAMC"
                                    append="%"
                                    placeholder="0.00"
                                    formatter={value => formatTwoDecimalPlaces(value)}
                                />
                            )}
                        />
                    </IressCol>
                )}
            </IressRow>
        </>
    );
};
